import { useEffect, useState } from 'react'

import AsyncStorage from '@react-native-async-storage/async-storage'

import { DEFAULT_THEME, Theme } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/theme'

import { IS_WEB, LYRA_THEME_STORAGE_KEY } from '../constants'

// TODO: This hook is a temporary storage solution for the themeType type using local storage.
// Planning to remove once we have a DB persistence solution

const isTheme = (value: string | null): value is Theme => Object.values(Theme).includes(value as Theme)

const readStoredWebTheme = (): Theme => {
  try {
    const storedTheme = localStorage.getItem(LYRA_THEME_STORAGE_KEY)
    return isTheme(storedTheme) ? storedTheme : DEFAULT_THEME
  } catch {
    return DEFAULT_THEME
  }
}

/*
 * The theme selection lives in a module-level store rather than in `useState` so that every caller
 * of this hook observes the same value. Per-hook state gave each caller its own copy: changing the
 * theme from settings updated ThemeProvider's copy but left ProductTypeProvider's copy on the stale
 * value, which is why Essentials kept requesting `aurora_light` with dark mode on (CEA-3988).
 * ProductTypeProvider cannot simply read the styled-components theme instead, because ThemeProvider
 * consumes ProductTypeContext and therefore has to render below it.
 */
let currentTheme: Theme = IS_WEB ? readStoredWebTheme() : DEFAULT_THEME
let hasHydrated = IS_WEB

const listeners = new Set<() => void>()

const getSnapshot = () => currentTheme

const setCurrentTheme = (theme: Theme) => {
  if (currentTheme === theme) {
    return
  }
  currentTheme = theme
  listeners.forEach((listener) => listener())
}

/**
 * Native reads the persisted theme asynchronously; web reads it synchronously above.
 */
const hydrateFromStorage = () => {
  if (hasHydrated) {
    return
  }
  hasHydrated = true
  AsyncStorage.getItem(LYRA_THEME_STORAGE_KEY)
    .then((storedTheme) => {
      setCurrentTheme(isTheme(storedTheme) ? storedTheme : DEFAULT_THEME)
    })
    .catch((error) => {
      console.error('Failed to read theme from AsyncStorage', error)
    })
}

// Start the read at import time so the persisted theme is in place well before any consumer that
// hands it off to a webview (Essentials) mounts.
hydrateFromStorage()

const subscribe = (listener: () => void) => {
  hydrateFromStorage()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Persist only on an explicit selection. The previous implementation wrote on mount, which let a
 * freshly mounted consumer race the read and overwrite a stored theme with the default.
 */
export const setThemeType = (theme: Theme) => {
  if (currentTheme === theme) {
    return
  }
  setCurrentTheme(theme)
  if (IS_WEB) {
    try {
      localStorage.setItem(LYRA_THEME_STORAGE_KEY, theme)
    } catch (error) {
      console.error('Failed to persist theme to localStorage', error)
    }
  } else {
    AsyncStorage.setItem(LYRA_THEME_STORAGE_KEY, theme)
  }
}

export const useThemeSettings = () => {
  const [themeType, setLocalThemeType] = useState(getSnapshot)

  useEffect(() => {
    const onChange = () => setLocalThemeType(getSnapshot())
    // Catch a change - notably the async hydration - that landed between render and subscribe.
    onChange()
    return subscribe(onChange)
  }, [])

  return { themeType, setThemeType }
}
